import { useState, useEffect, useReducer, useRef, type ReactNode } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import {
  X,
  Plus,
  Search,
  ChevronDown,
  Zap,
  RotateCcw,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  LayoutGrid,
  Tag,
  CalendarClock,
} from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listMenuCategories,
  listAllMenuItemsByCategory,
  listMenuItems,
  listStoreTargetedDealItems,
  listAppointmentServices,
} from "@/lib/api/admin";
import type { MenuCategory, MenuItem, TargetDeal, AppointmentService } from "@/lib/api/types";
import {
  formToSchedule,
  scheduleToForm,
  validateRawSchedule,
  type ScheduleFormState,
} from "@/lib/utils/schedule";
import { DealScheduleInput } from "@/components/schedule/DealScheduleInput";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { DiscardDialog } from "@/components/DiscardDialog";
import { Spinner } from "@/components/Spinner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatCents } from "@/lib/utils/number";
import { cn } from "@/lib/utils";

const DISCOUNT_SUGGESTIONS = ["10", "20", "50"];

// ─── Form state ────────────────────────────────────────────────────────────────

type DiscountInputMode = "percent" | "flat_price";

type TargetDealFormState = {
  name: string;
  discountInputMode: DiscountInputMode;
  discountPercent: string;
  flatPrice: string; // dollars, charged for every targeted item -- only used when discountInputMode === "flat_price"
  maxClaimsPerCustomerPerDay: string;
  schedule: ScheduleFormState;
  status: "active" | "inactive";
  selectedCategoryIds: string[];
  individualItems: MenuItem[];
  excludedItemIds: string[];
  serviceIds: string[];
};

function initForm(item?: TargetDeal): TargetDealFormState {
  if (!item) {
    return {
      name: "",
      discountInputMode: "percent",
      discountPercent: "",
      flatPrice: "",
      maxClaimsPerCustomerPerDay: "",
      schedule: scheduleToForm(),
      status: "active",
      selectedCategoryIds: [],
      individualItems: [],
      excludedItemIds: [],
      serviceIds: [],
    };
  }
  return {
    name: item.name,
    discountInputMode: item.discount_type === "fixed_price" ? "flat_price" : "percent",
    discountPercent: item.discount_percent != null ? String(item.discount_percent) : "",
    flatPrice: item.fixed_price_cents != null ? (item.fixed_price_cents / 100).toFixed(2) : "",
    maxClaimsPerCustomerPerDay:
      item.max_claims_per_customer_per_day != null
        ? String(item.max_claims_per_customer_per_day)
        : "",
    schedule: scheduleToForm(item.schedule),
    status: item.status,
    selectedCategoryIds: item.category_ids ?? [],
    individualItems: [],
    excludedItemIds: item.excluded_item_ids ?? [],
    serviceIds: item.appointment_service_ids ?? [],
  };
}

type FormAction =
  | { type: "reset"; item?: TargetDeal }
  | {
      type: "set";
      field: "name" | "discountPercent" | "flatPrice" | "maxClaimsPerCustomerPerDay";
      value: string;
    }
  | { type: "setDiscountInputMode"; mode: DiscountInputMode }
  | { type: "setStatus"; value: "active" | "inactive" }
  | { type: "setSchedule"; value: ScheduleFormState }
  | { type: "toggleCategory"; id: string }
  | { type: "seedResolved"; individualItems: MenuItem[] }
  | { type: "addIndividualItem"; item: MenuItem }
  | { type: "removeIndividualItem"; id: string }
  | { type: "toggleExcluded"; id: string }
  | { type: "toggleService"; id: string };

function formReducer(state: TargetDealFormState, action: FormAction): TargetDealFormState {
  switch (action.type) {
    case "reset":
      return initForm(action.item);
    case "set":
      return { ...state, [action.field]: action.value };
    case "setDiscountInputMode":
      return { ...state, discountInputMode: action.mode };
    case "setStatus":
      return { ...state, status: action.value };
    case "setSchedule":
      return { ...state, schedule: action.value };
    case "toggleCategory":
      return {
        ...state,
        selectedCategoryIds: state.selectedCategoryIds.includes(action.id)
          ? state.selectedCategoryIds.filter((x) => x !== action.id)
          : [...state.selectedCategoryIds, action.id],
      };
    case "seedResolved":
      return { ...state, individualItems: action.individualItems };
    case "addIndividualItem":
      if (state.individualItems.some((i) => i.id === action.item.id)) return state;
      return {
        ...state,
        individualItems: [...state.individualItems, action.item],
        // Explicit add always wins over exclusion.
        excludedItemIds: state.excludedItemIds.filter((id) => id !== action.item.id),
      };
    case "removeIndividualItem":
      return {
        ...state,
        individualItems: state.individualItems.filter((i) => i.id !== action.id),
      };
    case "toggleExcluded":
      return {
        ...state,
        excludedItemIds: state.excludedItemIds.includes(action.id)
          ? state.excludedItemIds.filter((x) => x !== action.id)
          : [...state.excludedItemIds, action.id],
      };
    case "toggleService":
      return {
        ...state,
        serviceIds: state.serviceIds.includes(action.id)
          ? state.serviceIds.filter((x) => x !== action.id)
          : [...state.serviceIds, action.id],
      };
    default:
      return state;
  }
}

function parsePercent(val: string): number | null {
  if (val.trim() === "") return null;
  const n = parseFloat(val);
  if (isNaN(n) || n <= 0 || n > 100) return null;
  return n;
}

function parseFlatPriceCents(val: string): number | null {
  if (val.trim() === "") return null;
  const n = parseFloat(val);
  if (isNaN(n) || n <= 0) return null;
  return Math.round(n * 100);
}

function discountedCents(priceCents: number, pct: number): number {
  return Math.max(0, Math.round(priceCents * (1 - pct / 100)));
}

function ItemRow({
  item,
  caption,
  dimmed,
  actionIcon,
  actionLabel,
  onAction,
}: {
  item: MenuItem;
  caption?: string;
  dimmed?: boolean;
  actionIcon: ReactNode;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 ${dimmed ? "opacity-60" : ""}`}>
      {item.image_url ? (
        <img
          src={item.image_url}
          alt={item.name}
          className="size-9 rounded-md object-cover shrink-0"
        />
      ) : (
        <div className="size-9 rounded-md bg-muted shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.name}</p>
        {caption && <p className="text-[10px] text-muted-foreground">{caption}</p>}
      </div>
      <button
        type="button"
        onClick={onAction}
        className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
        aria-label={actionLabel}
      >
        {actionIcon}
      </button>
    </div>
  );
}

// ─── Target Deal Form Dialog ───────────────────────────────────────────────

type TargetDealFormDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  storeId: string;
  item?: TargetDeal;
  canManageAppointments: boolean;
  onSave: (data: {
    name: string;
    discount_type: "percent" | "fixed_price";
    discount_percent: number | null;
    fixed_price_cents: number | null;
    max_claims_per_customer_per_day: number | null;
    schedule: Record<string, { open: string; close: string }[]>;
    status: "active" | "inactive";
    category_ids: string[];
    item_ids: string[];
    excluded_item_ids: string[];
    appointment_service_ids: string[];
  }) => void;
  isPending: boolean;
  apiError?: string | null;
};

export function TargetDealFormDialog({
  open,
  onOpenChange,
  storeId,
  item,
  canManageAppointments,
  onSave,
  isPending,
  apiError,
}: TargetDealFormDialogProps) {
  const user = useAdminAuth();
  const isEditing = !!item;

  const [state, dispatch] = useReducer(formReducer, undefined, () => initForm(item));
  const [dirty, setDirty] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hasSeededResolved, setHasSeededResolved] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const [nameError, setNameError] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [maxClaimsError, setMaxClaimsError] = useState<string | null>(null);
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});
  const [scheduleFormError, setScheduleFormError] = useState<string | null>(null);
  const [targetsError, setTargetsError] = useState<string | null>(null);

  // Reset all local state whenever the dialog opens for a (possibly new) item.
  useEffect(() => {
    if (!open) return;
    dispatch({ type: "reset", item });
    setDirty(false);
    setDiscardOpen(false);
    setPickerOpen(false);
    setHasSeededResolved(!item); // new deals need no async seed
    setHasAttemptedSubmit(false);
    setNameError(null);
    setDiscountError(null);
    setMaxClaimsError(null);
    setScheduleErrors({});
    setScheduleFormError(null);
    setTargetsError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const { data: categories = [] } = useQuery({
    queryKey: ["menu-categories", storeId],
    queryFn: () => listMenuCategories(user!, storeId),
    enabled: !!user && open,
  });

  // One-time seed of individually-added items; category edits recompute via categoryItemQueries below.
  const resolvedQuery = useQuery({
    queryKey: ["target-deal-targeted-items", storeId, item?.id],
    queryFn: () => listStoreTargetedDealItems(user!, storeId, item!.id),
    enabled: !!user && open && isEditing,
  });

  useEffect(() => {
    if (open && isEditing && resolvedQuery.data && !hasSeededResolved) {
      const individualItems = resolvedQuery.data
        .filter((r) => r.source === "explicit")
        .map((r) => r.menu_item);
      dispatch({ type: "seedResolved", individualItems });
      setHasSeededResolved(true);
    }
  }, [open, isEditing, resolvedQuery.data, hasSeededResolved]);

  // Live-resolved category membership preview, recomputed client-side on category selection change.
  const categoryItemQueries = useQueries({
    queries: state.selectedCategoryIds.map((categoryId) => ({
      queryKey: ["target-deal-category-items", storeId, categoryId],
      queryFn: () => listAllMenuItemsByCategory(user!, storeId, categoryId),
      enabled: !!user && open,
      staleTime: 30_000,
    })),
  });
  const categoryItemsLoading = categoryItemQueries.some((q) => q.isLoading);

  const individualIds = new Set(state.individualItems.map((i) => i.id));
  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));
  const excludedIds = new Set(state.excludedItemIds);
  // Explicit adds win over an exclusion, so seenIds starts from individualIds.
  const seenIds = new Set(individualIds);
  const categoryGroups: {
    categoryId: string;
    categoryName: string;
    included: MenuItem[];
    excluded: MenuItem[];
  }[] = [];
  state.selectedCategoryIds.forEach((categoryId, idx) => {
    const items = categoryItemQueries[idx]?.data ?? [];
    const categoryName = categoryNameById.get(categoryId) ?? "Category";
    const included = items.filter((item) => !excludedIds.has(item.id));
    const excluded = items.filter((item) => excludedIds.has(item.id));
    categoryGroups.push({ categoryId, categoryName, included, excluded });
    for (const it of included) {
      seenIds.add(it.id);
    }
  });
  const totalTargetedCount = seenIds.size;

  // Target-services checklist data; gated on canManageAppointments so a disabled store's stale targeting stays hidden.
  const { data: services = [], isLoading: servicesLoading } = useQuery({
    queryKey: ["appointment-services", storeId],
    queryFn: () => listAppointmentServices(user!, storeId),
    enabled: !!user && open && canManageAppointments,
  });
  const selectedServices = services.filter((s) => state.serviceIds.includes(s.id));

  // ── Derived preview data ──────────────────────────────────────────────
  const discountPercent = parsePercent(state.discountPercent);
  const flatPriceCents = parseFlatPriceCents(state.flatPrice);
  const hasValidDiscount =
    state.discountInputMode === "percent" ? discountPercent !== null : flatPriceCents !== null;
  const previewDiscountedCents = (regularCents: number) =>
    state.discountInputMode === "percent"
      ? discountedCents(regularCents, discountPercent!)
      : flatPriceCents!;
  const hasTargets = totalTargetedCount > 0 || state.serviceIds.length > 0;
  const hasSchedule = Object.keys(formToSchedule(state.schedule)).length > 0;
  const isReadyToPublish = hasValidDiscount && hasTargets && hasSchedule;

  const nextRequiredStep = !hasValidDiscount
    ? "Set a discount between 1 and 100%"
    : !hasTargets
      ? `Target at least one item, category${canManageAppointments ? ", or service" : ""}`
      : !hasSchedule
        ? "Choose at least one active day"
        : null;

  // A flat, de-duplicated list of the menu items this deal actually targets, for the customer preview.
  const previewItems: MenuItem[] = [];
  const previewSeen = new Set<string>();
  for (const it of state.individualItems) {
    if (!previewSeen.has(it.id)) {
      previewSeen.add(it.id);
      previewItems.push(it);
    }
  }
  for (const group of categoryGroups) {
    for (const it of group.included) {
      if (!previewSeen.has(it.id)) {
        previewSeen.add(it.id);
        previewItems.push(it);
      }
    }
  }

  const inlineDiscountError: string | null =
    discountError ??
    (state.discountInputMode === "percent"
      ? state.discountPercent.trim() === ""
        ? null
        : discountPercent === null
          ? "Enter a discount between 1 and 100%"
          : null
      : state.flatPrice.trim() === ""
        ? null
        : flatPriceCents === null
          ? "Enter a price greater than $0"
          : null);

  const targetsLoading = categoryItemsLoading || (isEditing && !hasSeededResolved);

  const hasAnySelection =
    state.selectedCategoryIds.length > 0 ||
    state.individualItems.length > 0 ||
    state.serviceIds.length > 0;
  // Wait out targetsLoading first — an edited deal seeds individual items async.
  const showTargetsEmpty = !hasAnySelection && !targetsLoading;

  const busy = isPending;

  function tryClose() {
    if (dirty) {
      setDiscardOpen(true);
    } else {
      onOpenChange(false);
    }
  }

  function handleSave() {
    setHasAttemptedSubmit(true);
    setNameError(null);
    setDiscountError(null);
    setMaxClaimsError(null);
    setScheduleErrors({});
    setScheduleFormError(null);
    setTargetsError(null);

    const name = state.name.trim();
    if (name.length < 2 || name.length > 100) {
      setNameError("Name must be between 2 and 100 characters.");
      return;
    }

    if (state.discountInputMode === "percent") {
      const parsed = parseFloat(state.discountPercent);
      if (isNaN(parsed) || parsed <= 0 || parsed > 100) {
        setDiscountError("Discount must be greater than 0 and at most 100%.");
        return;
      }
    } else {
      if (flatPriceCents === null) {
        setDiscountError("Enter a price greater than $0.");
        return;
      }
    }

    if (!hasTargets) {
      setTargetsError(
        `Target at least one item, category${canManageAppointments ? ", or service" : ""}.`
      );
      return;
    }

    let maxClaimsPerCustomerPerDay: number | null = null;
    if (state.maxClaimsPerCustomerPerDay.trim() !== "") {
      const parsed = parseInt(state.maxClaimsPerCustomerPerDay, 10);
      if (isNaN(parsed) || parsed < 1) {
        setMaxClaimsError("Must be at least 1, or blank for unlimited.");
        return;
      }
      maxClaimsPerCustomerPerDay = parsed;
    }

    const rawSchedule = formToSchedule(state.schedule);
    if (Object.keys(rawSchedule).length === 0) {
      setScheduleFormError("Select at least one day.");
      return;
    }
    const schedErrs = validateRawSchedule(rawSchedule);
    if (schedErrs) {
      setScheduleErrors(schedErrs);
      return;
    }

    onSave({
      name,
      discount_type: state.discountInputMode === "flat_price" ? "fixed_price" : "percent",
      discount_percent:
        state.discountInputMode === "percent" ? parseFloat(state.discountPercent) : null,
      fixed_price_cents: state.discountInputMode === "flat_price" ? flatPriceCents : null,
      max_claims_per_customer_per_day: maxClaimsPerCustomerPerDay,
      schedule: rawSchedule,
      status: state.status,
      category_ids: state.selectedCategoryIds,
      item_ids: state.individualItems.map((it) => it.id),
      excluded_item_ids: state.excludedItemIds,
      appointment_service_ids: state.serviceIds,
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (!o ? tryClose() : onOpenChange(o))}>
        <DialogContent
          showCloseButton={false}
          className="max-w-4xl sm:max-w-4xl p-0 gap-0 overflow-hidden flex flex-col"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">
            {isEditing ? "Edit Target Deal" : "Create Target Deal"}
          </DialogTitle>

          {/* Header */}
          <div className="flex items-center justify-between px-7 py-4 border-b shrink-0">
            <div className="flex items-center gap-3.5">
              <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Zap className="size-5 text-primary" />
              </div>
              <div>
                <span className="text-base font-semibold">
                  {isEditing ? "Edit Target Deal" : "Create Target Deal"}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Discount whole categories or hand-picked items on a recurring schedule
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  dispatch({ type: "reset", item });
                  setDirty(false);
                }}
                disabled={busy}
                className="gap-1.5 text-muted-foreground h-8 px-2.5"
              >
                <RotateCcw className="size-3.5" />
                Reset
              </Button>
              <button
                type="button"
                onClick={tryClose}
                className="rounded-md p-1.5 hover:bg-accent text-muted-foreground transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          {/* Body + Footer */}
          <form
            onSubmit={(e) => e.preventDefault()}
            noValidate
            className="flex flex-col flex-1 min-h-0"
          >
            <div className="flex flex-row flex-1 min-h-0 overflow-hidden">
              {/* Left: form */}
              <div className="flex-1 min-w-0 overflow-y-auto px-7 pt-5 pb-5 space-y-5">
                {/* Name */}
                <div className="space-y-1.5">
                  <Label htmlFor="td-name">
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="td-name"
                    value={state.name}
                    onChange={(e) => {
                      setDirty(true);
                      dispatch({ type: "set", field: "name", value: e.target.value });
                    }}
                    maxLength={100}
                    placeholder="e.g. Weekday Happy Hour"
                    disabled={busy}
                    aria-invalid={!!nameError}
                  />
                  {nameError && <p className="text-xs text-destructive">{nameError}</p>}
                  <p
                    className={`text-xs text-right ${
                      state.name.length > 100 ? "text-destructive" : "text-muted-foreground"
                    }`}
                  >
                    {state.name.length}/100
                  </p>
                </div>

                {/* Discount */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="td-discount">
                      Discount <span className="text-destructive">*</span>
                    </Label>
                    <SegmentedToggle
                      value={state.discountInputMode}
                      onChange={(mode) => {
                        setDirty(true);
                        dispatch({ type: "setDiscountInputMode", mode });
                      }}
                      options={[
                        { value: "percent", label: "% Rate" },
                        { value: "flat_price", label: "Flat Price" },
                      ]}
                      disabled={busy}
                    />
                  </div>

                  {state.discountInputMode === "percent" ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="relative w-32">
                        <Input
                          id="td-discount"
                          type="text"
                          inputMode="decimal"
                          placeholder="e.g. 20"
                          value={state.discountPercent}
                          onChange={(e) => {
                            setDirty(true);
                            dispatch({
                              type: "set",
                              field: "discountPercent",
                              value: e.target.value
                                .replace(/[^\d.]/g, "")
                                .replace(/(\..*)\./g, "$1"),
                            });
                          }}
                          disabled={busy}
                          className="w-full pr-8"
                          aria-invalid={!!discountError}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                          %
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {DISCOUNT_SUGGESTIONS.map((value) => {
                          const isActive = state.discountPercent === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              disabled={busy}
                              onClick={() => {
                                setDirty(true);
                                dispatch({ type: "set", field: "discountPercent", value });
                              }}
                              className={cn(
                                "text-xs border rounded-md px-2.5 h-8 transition-colors disabled:opacity-40",
                                isActive
                                  ? "border-primary bg-primary/5 text-primary font-medium"
                                  : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                              )}
                            >
                              {value}%
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="relative w-40">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none select-none">
                        $
                      </span>
                      <Input
                        id="td-flat-price"
                        type="text"
                        inputMode="decimal"
                        placeholder="e.g. 15.00"
                        value={state.flatPrice}
                        onChange={(e) => {
                          setDirty(true);
                          dispatch({
                            type: "set",
                            field: "flatPrice",
                            value: e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"),
                          });
                        }}
                        disabled={busy}
                        fullWidth={false}
                        className="w-full pl-6"
                        aria-invalid={!!discountError}
                      />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {state.discountInputMode === "percent"
                      ? `Applied to every targeted item${canManageAppointments ? " & service" : ""}`
                      : `Every targeted item${canManageAppointments ? " & service" : ""} is charged this exact price`}
                  </p>
                  {inlineDiscountError && (
                    <p className="text-xs text-destructive">{inlineDiscountError}</p>
                  )}
                </div>

                {/* Deal Targets */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>
                      Deal Targets <span className="text-destructive">*</span>
                    </Label>
                    {hasAnySelection && (
                      <span className="text-xs text-muted-foreground">
                        {state.selectedCategoryIds.length} categor
                        {state.selectedCategoryIds.length === 1 ? "y" : "ies"} ·{" "}
                        {state.individualItems.length} item
                        {state.individualItems.length === 1 ? "" : "s"}
                        {canManageAppointments &&
                          ` · ${state.serviceIds.length} service${state.serviceIds.length === 1 ? "" : "s"}`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground -mt-0.5">
                    Choose the categories
                    {canManageAppointments
                      ? ", individual items, or appointment services"
                      : " or individual items"}{" "}
                    this discount applies to. Customers get the deal rate only through the dedicated
                    deal page in the app — everywhere else, targeted items still charge their
                    regular price.
                  </p>

                  {targetsLoading && (
                    <div className="flex justify-center py-6">
                      <Spinner className="size-5 border-primary" />
                    </div>
                  )}

                  {showTargetsEmpty && (
                    <div className="border border-dashed rounded-md py-7 px-4 text-center space-y-3">
                      <Tag className="size-6 text-muted-foreground/40 mx-auto" />
                      <p className="text-xs text-muted-foreground">Nothing targeted yet.</p>
                      <Button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        disabled={busy}
                        className="gap-1.5"
                      >
                        <Plus className="size-4" />
                        Add Targets
                      </Button>
                    </div>
                  )}

                  {!targetsLoading && hasAnySelection && (
                    <div className="border rounded-md divide-y divide-border">
                      {state.individualItems.map((it) => (
                        <ItemRow
                          key={it.id}
                          item={it}
                          caption="Individually added"
                          actionIcon={<X className="size-3.5" />}
                          actionLabel="Remove item"
                          onAction={() => {
                            setDirty(true);
                            dispatch({ type: "removeIndividualItem", id: it.id });
                          }}
                        />
                      ))}
                      {categoryGroups.map((group) => (
                        <Popover key={group.categoryId}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-2 px-3 py-2 w-full text-left hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {group.categoryName} ({group.included.length})
                                </p>
                                <p className="text-[10px] text-muted-foreground">Category</p>
                              </div>
                              <ChevronDown className="size-3.5 text-muted-foreground shrink-0" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="w-72 max-h-80 overflow-y-auto p-2"
                          >
                            <p className="text-xs font-semibold px-1 pb-1.5">
                              {group.categoryName}
                            </p>
                            {group.included.length === 0 && group.excluded.length === 0 ? (
                              <p className="text-xs text-muted-foreground px-1 py-1">
                                No items in this category yet.
                              </p>
                            ) : (
                              <>
                                {group.included.length === 0 ? (
                                  <p className="text-xs text-muted-foreground px-1 py-1">
                                    No items included from this category.
                                  </p>
                                ) : (
                                  <div className="divide-y divide-border rounded-md border">
                                    {group.included.map((it) => (
                                      <ItemRow
                                        key={it.id}
                                        item={it}
                                        actionIcon={<X className="size-3.5" />}
                                        actionLabel="Exclude item"
                                        onAction={() => {
                                          setDirty(true);
                                          dispatch({ type: "toggleExcluded", id: it.id });
                                        }}
                                      />
                                    ))}
                                  </div>
                                )}
                                {group.excluded.length > 0 && (
                                  <>
                                    <p className="text-xs font-semibold px-1 pt-3 pb-1.5">
                                      Excluded ({group.excluded.length})
                                    </p>
                                    <div className="divide-y divide-border rounded-md border">
                                      {group.excluded.map((it) => (
                                        <ItemRow
                                          key={it.id}
                                          item={it}
                                          dimmed
                                          actionIcon={<Plus className="size-3.5" />}
                                          actionLabel="Add back"
                                          onAction={() => {
                                            setDirty(true);
                                            dispatch({ type: "toggleExcluded", id: it.id });
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                          </PopoverContent>
                        </Popover>
                      ))}
                      {selectedServices.map((service) => (
                        <div key={service.id} className="flex items-center gap-2 px-3 py-2">
                          <div className="size-9 rounded-md bg-muted flex items-center justify-center shrink-0">
                            <CalendarClock className="size-4 text-muted-foreground/50" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{service.name}</p>
                            <p className="text-[10px] text-muted-foreground">
                              Appointment service{!service.active ? " · Inactive" : ""}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {formatCents(service.price_cents)}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setDirty(true);
                              dispatch({ type: "toggleService", id: service.id });
                            }}
                            className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                            aria-label="Remove service"
                          >
                            <X className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {!targetsLoading && hasAnySelection && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPickerOpen(true)}
                      disabled={busy}
                      className="w-full gap-1.5 border-dashed"
                    >
                      <Plus className="size-4" />
                      Add or manage targets
                    </Button>
                  )}
                  {targetsError && <p className="text-xs text-destructive">{targetsError}</p>}
                </div>

                {/* Schedule */}
                <div className="border-y py-5 bg-muted-foreground/[0.07] -mx-7 px-7">
                  <DealScheduleInput
                    schedule={formToSchedule(state.schedule)}
                    onChange={(raw) => {
                      setDirty(true);
                      dispatch({ type: "setSchedule", value: scheduleToForm(raw) });
                      setScheduleErrors({});
                      setScheduleFormError(null);
                    }}
                    error={scheduleFormError ?? Object.values(scheduleErrors)[0]}
                  />
                </div>

                {/* Max claims */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Max claims per customer per day</span>
                    <span className="text-xs text-muted-foreground">Prevents promotion abuse</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setDirty(true);
                        dispatch({
                          type: "set",
                          field: "maxClaimsPerCustomerPerDay",
                          value: "",
                        });
                      }}
                      disabled={busy}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                        state.maxClaimsPerCustomerPerDay === ""
                          ? "border-primary"
                          : "border-border hover:border-primary/40"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 size-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                          state.maxClaimsPerCustomerPerDay === ""
                            ? "border-primary"
                            : "border-muted-foreground/40"
                        )}
                      >
                        {state.maxClaimsPerCustomerPerDay === "" && (
                          <span className="size-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">Unlimited</p>
                        <p className="text-xs text-muted-foreground">No daily order restriction</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDirty(true);
                        if (state.maxClaimsPerCustomerPerDay === "")
                          dispatch({
                            type: "set",
                            field: "maxClaimsPerCustomerPerDay",
                            value: "1",
                          });
                      }}
                      disabled={busy}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                        state.maxClaimsPerCustomerPerDay !== ""
                          ? "border-primary"
                          : "border-border hover:border-primary/40"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 size-4 shrink-0 rounded-full border-2 flex items-center justify-center",
                          state.maxClaimsPerCustomerPerDay !== ""
                            ? "border-primary"
                            : "border-muted-foreground/40"
                        )}
                      >
                        {state.maxClaimsPerCustomerPerDay !== "" && (
                          <span className="size-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">Limit Per User</p>
                        <p className="text-xs text-muted-foreground">Fixed number of redemptions</p>
                      </div>
                    </button>
                  </div>
                  {state.maxClaimsPerCustomerPerDay !== "" && (
                    <div className="flex items-center gap-3">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={state.maxClaimsPerCustomerPerDay}
                        onChange={(e) => {
                          setDirty(true);
                          dispatch({
                            type: "set",
                            field: "maxClaimsPerCustomerPerDay",
                            value: e.target.value.replace(/\D/g, ""),
                          });
                        }}
                        disabled={busy}
                        className="w-20"
                        aria-invalid={!!maxClaimsError}
                      />
                      <span className="text-sm text-muted-foreground">
                        redemption(s) per guest per 24 hours
                      </span>
                    </div>
                  )}
                  {maxClaimsError && <p className="text-xs text-destructive">{maxClaimsError}</p>}
                </div>

                {/* Status (edit only) */}
                {isEditing && (
                  <div className="space-y-1.5">
                    <Label>Status</Label>
                    <SegmentedToggle
                      value={state.status}
                      onChange={(s) => {
                        setDirty(true);
                        dispatch({ type: "setStatus", value: s });
                      }}
                      options={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" },
                      ]}
                    />
                  </div>
                )}

                {apiError && <p className="text-sm text-destructive">{apiError}</p>}
              </div>

              {/* Right: live preview */}
              <div className="w-[360px] shrink-0 border-l overflow-y-auto bg-slate-100">
                <div className="px-5 py-5 space-y-4">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    <span className="size-2 rounded-full bg-green-500 shrink-0" />
                    Live Customer Preview
                  </div>

                  {!hasValidDiscount || !hasTargets ? (
                    <div className="rounded-xl border border-dashed border-slate-300 h-52 flex items-center justify-center">
                      <p className="text-sm text-slate-400 text-center px-4">
                        Set a discount and target items to see a live preview
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Deal card */}
                      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                        <div className="relative bg-gradient-to-br from-primary to-red-700 px-4 py-5 text-white">
                          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/80">
                            <Zap className="size-3 fill-current" />
                            Target Deal
                          </div>
                          <p className="text-3xl font-extrabold leading-none mt-1.5">
                            {state.discountInputMode === "percent"
                              ? `${Math.round(discountPercent!)}% OFF`
                              : `${formatCents(flatPriceCents!)} EACH`}
                          </p>
                          <p className="text-xs text-white/80 mt-1 line-clamp-1">
                            {state.name.trim() || "Untitled deal"}
                          </p>
                        </div>

                        <div className="p-4 space-y-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            {totalTargetedCount + selectedServices.length} on deal
                          </p>
                          {previewItems.length === 0 && selectedServices.length === 0 ? (
                            <p className="text-xs text-slate-400">
                              Category items load here as they resolve.
                            </p>
                          ) : (
                            <div className="space-y-2.5">
                              {previewItems.slice(0, 4).map((it) => (
                                <div key={it.id} className="flex items-center gap-2.5">
                                  {it.image_url ? (
                                    <img
                                      src={it.image_url}
                                      alt={it.name}
                                      className="size-9 rounded-md object-cover shrink-0"
                                    />
                                  ) : (
                                    <div className="size-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                                      <Tag className="size-4 text-slate-300" />
                                    </div>
                                  )}
                                  <span className="flex-1 min-w-0 text-xs text-slate-700 truncate">
                                    {it.name}
                                  </span>
                                  <div className="flex items-baseline gap-1.5 shrink-0">
                                    <span className="text-[11px] text-slate-400 line-through">
                                      {formatCents(it.price_cents)}
                                    </span>
                                    <span className="text-xs font-semibold text-primary">
                                      {formatCents(previewDiscountedCents(it.price_cents))}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {selectedServices
                                .slice(0, previewItems.length >= 4 ? 0 : 2)
                                .map((service) => (
                                  <div key={service.id} className="flex items-center gap-2.5">
                                    <div className="size-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                                      <CalendarClock className="size-4 text-slate-300" />
                                    </div>
                                    <span className="flex-1 min-w-0 text-xs text-slate-700 truncate">
                                      {service.name}
                                    </span>
                                    <div className="flex items-baseline gap-1.5 shrink-0">
                                      <span className="text-[11px] text-slate-400 line-through">
                                        {formatCents(service.price_cents)}
                                      </span>
                                      <span className="text-xs font-semibold text-primary">
                                        {formatCents(previewDiscountedCents(service.price_cents))}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              {totalTargetedCount + selectedServices.length >
                                previewItems.slice(0, 4).length +
                                  selectedServices.slice(0, previewItems.length >= 4 ? 0 : 2)
                                    .length && (
                                <p className="text-[11px] text-slate-400 pt-0.5">
                                  +
                                  {totalTargetedCount +
                                    selectedServices.length -
                                    (previewItems.slice(0, 4).length +
                                      selectedServices.slice(0, previewItems.length >= 4 ? 0 : 2)
                                        .length)}{" "}
                                  more on deal
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Deal Scope */}
                      <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                            <LayoutGrid className="size-3.5 text-slate-400" />
                            Deal Scope
                          </div>
                        </div>
                        <div
                          className={cn(
                            "grid gap-2 text-center",
                            canManageAppointments ? "grid-cols-3" : "grid-cols-2"
                          )}
                        >
                          <div className="rounded-lg bg-slate-50 p-2">
                            <p className="text-[10px] text-slate-400 mb-1">Items</p>
                            <p className="text-sm font-semibold text-slate-800">
                              {totalTargetedCount}
                            </p>
                          </div>
                          <div className="rounded-lg bg-slate-50 p-2">
                            <p className="text-[10px] text-slate-400 mb-1">Categories</p>
                            <p className="text-sm font-semibold text-slate-800">
                              {state.selectedCategoryIds.length}
                            </p>
                          </div>
                          {canManageAppointments && (
                            <div className="rounded-lg bg-slate-50 p-2">
                              <p className="text-[10px] text-slate-400 mb-1">Services</p>
                              <p className="text-sm font-semibold text-slate-800">
                                {state.serviceIds.length}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-7 py-3.5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-1.5 text-xs">
                {isReadyToPublish ? (
                  <>
                    <CheckCircle className="size-3.5 text-green-600 shrink-0" />
                    <span className="text-muted-foreground">
                      Validation passed • Ready to publish
                    </span>
                  </>
                ) : hasAttemptedSubmit ? (
                  <div className="flex items-center gap-1.5 bg-destructive/10 text-destructive border border-destructive/20 rounded-md px-2.5 py-1">
                    <AlertCircle className="size-3.5 shrink-0" />
                    <span className="font-medium">{nextRequiredStep}</span>
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button type="button" variant="outline" onClick={tryClose} disabled={busy}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={busy}>
                      {busy ? "Saving..." : "Save Changes"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="button" variant="outline" onClick={tryClose} disabled={busy}>
                      Discard
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={busy} className="gap-1.5">
                      {busy ? (
                        "Publishing..."
                      ) : (
                        <>
                          <span>Publish Target Deal</span>
                          <ArrowRight className="size-4" />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <TargetPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        storeId={storeId}
        categories={categories}
        selectedCategoryIds={state.selectedCategoryIds}
        onToggleCategory={(id) => {
          setDirty(true);
          dispatch({ type: "toggleCategory", id });
        }}
        individualItems={state.individualItems}
        onAddItem={(it) => {
          setDirty(true);
          dispatch({ type: "addIndividualItem", item: it });
        }}
        onRemoveItem={(id) => {
          setDirty(true);
          dispatch({ type: "removeIndividualItem", id });
        }}
        services={services}
        servicesLoading={servicesLoading}
        serviceIds={state.serviceIds}
        onToggleService={(id) => {
          setDirty(true);
          dispatch({ type: "toggleService", id });
        }}
        canManageAppointments={canManageAppointments}
      />

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}

function TargetPickerDialog({
  open,
  onOpenChange,
  storeId,
  categories,
  selectedCategoryIds,
  onToggleCategory,
  individualItems,
  onAddItem,
  onRemoveItem,
  services,
  servicesLoading,
  serviceIds,
  onToggleService,
  canManageAppointments,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  categories: MenuCategory[];
  selectedCategoryIds: string[];
  onToggleCategory: (id: string) => void;
  individualItems: MenuItem[];
  onAddItem: (item: MenuItem) => void;
  onRemoveItem: (id: string) => void;
  services: AppointmentService[];
  servicesLoading: boolean;
  serviceIds: string[];
  onToggleService: (id: string) => void;
  canManageAppointments: boolean;
}) {
  const user = useAdminAuth();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setSearchInput("");
    setSearchTerm("");
  }, [open]);

  const searchResultsQuery = useQuery({
    queryKey: ["menu-items", storeId, "target-deal-search", searchTerm],
    queryFn: () => listMenuItems(user!, storeId, { search: searchTerm, limit: 20 }),
    enabled: !!user && open && searchTerm.trim() !== "",
  });

  const individualIds = new Set(individualItems.map((i) => i.id));
  const totalSelected = selectedCategoryIds.length + individualItems.length + serviceIds.length;
  // Only surface the Services tab when appointments are enabled and the store has services.
  const showServicesTab = canManageAppointments && (servicesLoading || services.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-lg sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Add deal targets</DialogTitle>

        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b shrink-0">
          <div className="min-w-0">
            <span className="text-base font-semibold">Add Targets</span>
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalSelected === 0
                ? `Pick categories, items${canManageAppointments ? ", or services" : ""} to discount`
                : `${totalSelected} selected`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md p-1.5 hover:bg-accent text-muted-foreground transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        <Tabs defaultValue="categories" className="flex flex-col flex-1 min-h-0 gap-0">
          <TabsList className="px-5 shrink-0">
            <TabsTrigger value="categories">
              Categories
              {selectedCategoryIds.length > 0 && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({selectedCategoryIds.length})
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="items">
              Items
              {individualItems.length > 0 && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({individualItems.length})
                </span>
              )}
            </TabsTrigger>
            {showServicesTab && (
              <TabsTrigger value="services">
                Services
                {serviceIds.length > 0 && (
                  <span className="ml-1 text-xs text-muted-foreground">({serviceIds.length})</span>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Categories */}
          <TabsContent value="categories" className="min-h-0 overflow-y-auto px-5 py-4">
            <p className="text-xs text-muted-foreground mb-3">
              Every item in a selected category is included automatically — items added later join
              the deal too. Fine-tune exclusions from the summary once you close this.
            </p>
            {categories.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No categories yet.</p>
            ) : (
              <div className="border rounded-md divide-y divide-border">
                {categories.map((cat) => {
                  const selected = selectedCategoryIds.includes(cat.id);
                  return (
                    <label
                      key={cat.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => onToggleCategory(cat.id)}
                      />
                      <span className="flex-1 min-w-0 text-sm truncate">{cat.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Items */}
          <TabsContent value="items" className="min-h-0 overflow-y-auto px-5 py-4 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <Input
                  ref={searchRef}
                  placeholder="Search items..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setSearchTerm(searchInput.trim());
                    }
                  }}
                  className="pl-8"
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setSearchTerm(searchInput.trim())}
              >
                Search
              </Button>
            </div>
            {searchTerm === "" ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Search your menu to add specific items.
              </p>
            ) : searchResultsQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="size-5 border-primary" />
              </div>
            ) : (searchResultsQuery.data?.data.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No items found.</p>
            ) : (
              <div className="border rounded-md divide-y divide-border">
                {searchResultsQuery.data?.data.map((it) => {
                  const added = individualIds.has(it.id);
                  return (
                    <div key={it.id} className="flex items-center gap-3 px-3 py-2">
                      {it.image_url ? (
                        <img
                          src={it.image_url}
                          alt={it.name}
                          className="size-9 rounded-md object-cover shrink-0"
                        />
                      ) : (
                        <div className="size-9 rounded-md bg-muted shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{it.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatCents(it.price_cents)}
                        </p>
                      </div>
                      {added ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => onRemoveItem(it.id)}
                          className="gap-1 h-8 shrink-0 text-muted-foreground hover:text-destructive"
                        >
                          <X className="size-3.5" />
                          Remove
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onAddItem(it)}
                          className="gap-1 h-8 shrink-0"
                        >
                          <Plus className="size-3.5" />
                          Add
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Services */}
          {showServicesTab && (
            <TabsContent value="services" className="min-h-0 overflow-y-auto px-5 py-4">
              <p className="text-xs text-muted-foreground mb-3">
                Services have no discount of their own — include one here to discount it during this
                deal&apos;s schedule.
              </p>
              {servicesLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner className="size-5 border-primary" />
                </div>
              ) : services.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No appointment services yet.
                </p>
              ) : (
                <div className="border rounded-md divide-y divide-border">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <Checkbox
                        checked={serviceIds.includes(service.id)}
                        onCheckedChange={() => onToggleService(service.id)}
                      />
                      <span className="flex-1 min-w-0 text-sm truncate">{service.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatCents(service.price_cents)}
                      </span>
                      {!service.active && (
                        <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full shrink-0">
                          Inactive
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Footer */}
        <div className="border-t px-5 py-3.5 flex items-center justify-between gap-3 shrink-0">
          <span className="text-xs text-muted-foreground">
            {selectedCategoryIds.length} categor{selectedCategoryIds.length === 1 ? "y" : "ies"} ·{" "}
            {individualItems.length} item{individualItems.length === 1 ? "" : "s"}
            {canManageAppointments &&
              ` · ${serviceIds.length} service${serviceIds.length === 1 ? "" : "s"}`}
          </span>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
